// apps/web/src/features/requests/useRequestsFilters.ts
// Issue #630. Persistent /requests filter state: URL search params are the
// primary/shareable surface, `localStorage['magimp:filters:requests']` is
// the per-browser fallback for return navigation. Search `q`, pagination
// `cursor`, and `limit` are NEVER persisted by this hook — the page merges
// trimmed local `q` into the wire filters on its own.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useRequestManagerOptions } from '@magimp/shared-hooks';
import type { RequestKind, RequestStatus } from '@magimp/shared';
import { useAuthStore } from '../../stores/auth.js';
import {
  applyCanonicalFilterParams,
  detectStatusFilterRole,
  getDefaultRequestListStatuses,
  hasPersistedFilterKey,
  parsePersistedFiltersFromQuery,
  serializePersistedFiltersToCanonicalQuery,
  type PersistedRequestFilters,
  type RequestFilters,
  type StatusFilterRole,
} from './filters.js';
import { preserveRequestDrawerParams } from './useRequestUrlState.js';

/**
 * @aiPurpose canonical localStorage key for the persisted `/requests`
 *   filter payload (issue #630). Single source — `useRequestsFilters`
 *   reads/writes through it and `auth/userCacheReset.ts` removes it on
 *   real identity boundary transitions.
 */
export const REQUESTS_FILTERS_STORAGE_KEY = 'magimp:filters:requests';

export interface AdvancedFilterState {
  dateFrom: string;
  dateTo: string;
  weightFrom: string;
  weightTo: string;
  volumeFrom: string;
  volumeTo: string;
  managerIds: string[];
}

export function countActiveAdvancedFilters(advanced: AdvancedFilterState): number {
  return (
    (advanced.dateFrom || advanced.dateTo ? 1 : 0) +
    (advanced.weightFrom || advanced.weightTo ? 1 : 0) +
    (advanced.volumeFrom || advanced.volumeTo ? 1 : 0) +
    (advanced.managerIds.length > 0 ? 1 : 0)
  );
}

const EMPTY_ADVANCED: AdvancedFilterState = {
  dateFrom: '',
  dateTo: '',
  weightFrom: '',
  weightTo: '',
  volumeFrom: '',
  volumeTo: '',
  managerIds: [],
};

function parseNumberInput(s: string): number | undefined {
  if (!s) return undefined;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

// WHY: HTML <input type="date"> emits `YYYY-MM-DD`; the wire schema
//   (`RequestListQuerySchema.dateFrom/dateTo`) requires `z.string().datetime()`.
//   v1 semantics: a calendar day is anchored in UTC, NOT the viewer's local
//   zone. Matches the existing inline conversion in `RequestsListPage` so
//   round-trip stays consistent for shared links.
function dateInputToIsoStart(d: string): string | undefined {
  if (!d) return undefined;
  return `${d}T00:00:00.000Z`;
}
function dateInputToIsoEnd(d: string): string | undefined {
  if (!d) return undefined;
  return `${d}T23:59:59.999Z`;
}
function isoToDateInput(iso: string | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

interface UiFiltersState {
  selectedStatuses: RequestStatus[];
  selectedSlaStates: ('hot' | 'overdue')[];
  unreadOnly: boolean;
  selectedKind: RequestKind | null;
  advanced: AdvancedFilterState;
}

function uiFromPersisted(p: PersistedRequestFilters): UiFiltersState {
  return {
    selectedStatuses: p.status ? [...p.status] : [],
    selectedSlaStates: p.slaState ? [...p.slaState] : [],
    unreadOnly: p.unread === true,
    selectedKind: p.kind ?? null,
    advanced: {
      dateFrom: isoToDateInput(p.dateFrom),
      dateTo: isoToDateInput(p.dateTo),
      weightFrom: p.weightFrom !== undefined ? String(p.weightFrom) : '',
      weightTo: p.weightTo !== undefined ? String(p.weightTo) : '',
      volumeFrom: p.volumeFrom !== undefined ? String(p.volumeFrom) : '',
      volumeTo: p.volumeTo !== undefined ? String(p.volumeTo) : '',
      managerIds: p.managerId ? [...p.managerId] : [],
    },
  };
}

function persistedFromUi(ui: UiFiltersState): PersistedRequestFilters {
  const out: PersistedRequestFilters = {};
  if (ui.selectedStatuses.length > 0) out.status = ui.selectedStatuses;
  if (ui.selectedSlaStates.length > 0) out.slaState = ui.selectedSlaStates;
  const dateFrom = dateInputToIsoStart(ui.advanced.dateFrom);
  const dateTo = dateInputToIsoEnd(ui.advanced.dateTo);
  if (dateFrom) out.dateFrom = dateFrom;
  if (dateTo) out.dateTo = dateTo;
  const wFrom = parseNumberInput(ui.advanced.weightFrom);
  const wTo = parseNumberInput(ui.advanced.weightTo);
  if (wFrom !== undefined) out.weightFrom = wFrom;
  if (wTo !== undefined) out.weightTo = wTo;
  const vFrom = parseNumberInput(ui.advanced.volumeFrom);
  const vTo = parseNumberInput(ui.advanced.volumeTo);
  if (vFrom !== undefined) out.volumeFrom = vFrom;
  if (vTo !== undefined) out.volumeTo = vTo;
  if (ui.advanced.managerIds.length > 0) out.managerId = ui.advanced.managerIds;
  if (ui.unreadOnly) out.unread = true;
  if (ui.selectedKind) out.kind = ui.selectedKind;
  return out;
}

/**
 * @aiPurpose derive the TanStack query-key shape from current persisted
 *   filters. Mirrors `urlToWireFilters` so the route loader's URL-derived
 *   prefetch key is structurally equal to the page's mount-time key under
 *   TanStack's structural hash. The single discrepancy is `q`, which the
 *   page merges in separately from local search.
 * @aiInvariant `status` is always populated; an empty `selectedStatuses`
 *   array (All chip) expands to `DEFAULT_REQUEST_LIST_STATUSES`.
 */
function sortStrings<T extends string>(arr: readonly T[]): T[] {
  return Array.from(new Set(arr)).sort();
}

function persistedToWireFilters(
  p: PersistedRequestFilters,
  role: StatusFilterRole,
): Partial<RequestFilters> {
  const wire: Partial<RequestFilters> = {};
  // INVARIANT (#630): multi-value arrays are sorted lexicographically so
  //   the on-mount key matches the URL-derived loader key under TanStack's
  //   structural array hash.
  // INVARIANT (#1159 PR5): the implicit-default fallback uses the
  //   role-aware status set so a logist's empty-selection wire key
  //   matches the loader's `urlToWireFilters(url, role)` output.
  wire.status =
    p.status && p.status.length > 0
      ? sortStrings(p.status)
      : sortStrings([...getDefaultRequestListStatuses(role)]);
  if (p.slaState && p.slaState.length > 0) wire.slaState = sortStrings(p.slaState);
  if (p.dateFrom) wire.dateFrom = p.dateFrom;
  if (p.dateTo) wire.dateTo = p.dateTo;
  if (p.weightFrom !== undefined) wire.weightFrom = p.weightFrom;
  if (p.weightTo !== undefined) wire.weightTo = p.weightTo;
  if (p.volumeFrom !== undefined) wire.volumeFrom = p.volumeFrom;
  if (p.volumeTo !== undefined) wire.volumeTo = p.volumeTo;
  if (p.managerId && p.managerId.length > 0) wire.managerId = sortStrings(p.managerId);
  if (p.unread === true) wire.unread = true;
  if (p.kind) wire.kind = p.kind;
  return wire;
}

function safeReadLocalStorage(): string | null {
  try {
    return localStorage.getItem(REQUESTS_FILTERS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeWriteLocalStorage(value: string): void {
  try {
    if (value === '') {
      localStorage.removeItem(REQUESTS_FILTERS_STORAGE_KEY);
    } else {
      localStorage.setItem(REQUESTS_FILTERS_STORAGE_KEY, value);
    }
  } catch {
    // best-effort
  }
}

function readInitialHydration(
  initialSearchParams: URLSearchParams,
  role: StatusFilterRole,
): {
  filters: PersistedRequestFilters;
  source: 'url' | 'storage' | 'defaults';
} {
  if (hasPersistedFilterKey(initialSearchParams)) {
    return {
      filters: parsePersistedFiltersFromQuery(initialSearchParams, role),
      source: 'url',
    };
  }
  const stored = safeReadLocalStorage();
  if (stored != null && stored.length > 0) {
    return {
      filters: parsePersistedFiltersFromQuery(new URLSearchParams(stored), role),
      source: 'storage',
    };
  }
  return { filters: {}, source: 'defaults' };
}

export interface UseRequestsFiltersInput {
  canPickManager: boolean;
}

export interface UseRequestsFiltersResult {
  selectedStatuses: RequestStatus[];
  toggleStatus: (id: 'all' | RequestStatus) => void;
  selectedSlaStates: ('hot' | 'overdue')[];
  toggleSlaState: (id: 'hot' | 'overdue') => void;
  unreadOnly: boolean;
  toggleUnread: () => void;
  // WHY (#1054): single-select kind chip. `null` when no chip is active
  //   (both kinds shown). Clicking the active chip clears the selection;
  //   clicking the other swaps the scalar value.
  selectedKind: RequestKind | null;
  toggleKind: (kind: RequestKind) => void;
  advanced: AdvancedFilterState;
  setAdvanced: (next: AdvancedFilterState) => void;
  clearAdvanced: () => void;
  /**
   * Wire-filter shape ready for `useRequestsInfinite` query keys. `null` while
   * the hydrated managerId set is still being validated against
   * `/requests/manager-options` — callers MUST treat null as "loading" and
   * skip the list query AND any cached fallback so a broad no-manager list
   * never flashes behind stale filter chips.
   */
  wireFilters: Partial<RequestFilters> | null;
}

/**
 * @aiPurpose persistent filter ownership for the `/requests` list page
 *   (issue #630). Hydrates from URL → localStorage → defaults, canonicalizes
 *   the URL with replace navigation, write-throughs to localStorage only
 *   when an authenticated user exists at write time, and gates the live
 *   list query until any hydrated managerId set has been validated against
 *   `/requests/manager-options`.
 * @aiInvariant the URL writer always funnels through
 *   `applyCanonicalFilterParams` + `preserveRequestDrawerParams` so drawer
 *   keys (`openId`, `tab`) — owned by `useRequestUrlState` — survive every
 *   filter mutation. The shape returned via `wireFilters` is identical to
 *   what `urlToWireFilters` produces in the route loader; otherwise the
 *   prefetch and on-mount query keys would diverge under TanStack's
 *   structural hash and warm-cache navigation would double-fetch.
 * @aiSecurity localStorage writes re-check `useAuthStore.getState().user`
 *   immediately before every put so an anonymous shared URL or a cross-tab
 *   logout race cannot reseed the persisted key after `userCacheReset`
 *   removed it.
 */
export function useRequestsFilters(input: UseRequestsFiltersInput): UseRequestsFiltersResult {
  const { canPickManager } = input;
  const [searchParams, setSearchParams] = useSearchParams();
  // INVARIANT (#1159 PR5): role is pinned at first render so the URL
  //   canonicalisation pass that drops hidden statuses fires once with
  //   the same role the wire-filter memo uses. A mid-session role swap
  //   is a logout-bounded event today (no in-app role swap), so a
  //   stable read on mount is sufficient.
  const role = detectStatusFilterRole(useAuthStore.getState().user);

  // INVARIANT: read once. The hook owns state from mount onward; external
  //   filter-URL writes are not supported in this iteration (drawer-only
  //   writes via `useRequestUrlState` survive untouched through
  //   `preserveRequestDrawerParams`).
  const initialRef = useRef<{
    filters: PersistedRequestFilters;
    source: 'url' | 'storage' | 'defaults';
  } | null>(null);
  if (initialRef.current === null) {
    initialRef.current = readInitialHydration(searchParams, role);
    if (!canPickManager && initialRef.current.filters.managerId) {
      // RBAC: actor cannot pick managers — strip any hydrated manager IDs
      // before they ever enter the wire/UI surface.
      const { managerId: _drop, ...rest } = initialRef.current.filters;
      initialRef.current = { filters: rest, source: initialRef.current.source };
    }
  }
  const initialPersisted = initialRef.current.filters;

  const [uiState, setUiState] = useState<UiFiltersState>(() => uiFromPersisted(initialPersisted));

  // Manager validation. Needed only when the hydrated payload contained at
  // least one managerId AND the actor can pick managers; otherwise the first
  // wire request can fire immediately.
  const needsManagerValidation = useMemo(
    () => canPickManager && (initialPersisted.managerId?.length ?? 0) > 0,
    [canPickManager, initialPersisted.managerId],
  );
  const [managerValidationPending, setManagerValidationPending] =
    useState<boolean>(needsManagerValidation);

  const managerOptionsQuery = useRequestManagerOptions({
    enabled: managerValidationPending,
    // WHY: cap retries so a slow/failing backend never traps the page in a
    //   long React Query retry spinner. On final failure we drop the
    //   hydrated manager IDs and continue with the rest of the filters.
    retry: managerValidationPending ? 1 : false,
  });

  useEffect(() => {
    if (!managerValidationPending) return;
    if (!canPickManager) {
      // Permission lost between hydration and effect run — drop IDs.
      setUiState((prev) => ({ ...prev, advanced: { ...prev.advanced, managerIds: [] } }));
      setManagerValidationPending(false);
      return;
    }
    if (managerOptionsQuery.isSuccess) {
      const visible = new Set(managerOptionsQuery.data.map((m) => m.id));
      setUiState((prev) => ({
        ...prev,
        advanced: {
          ...prev.advanced,
          managerIds: prev.advanced.managerIds.filter((id) => visible.has(id)),
        },
      }));
      setManagerValidationPending(false);
      return;
    }
    if (managerOptionsQuery.isError) {
      // After at-most-one retry the query gave up. Drop manager IDs and let
      // the rest of the filters render.
      setUiState((prev) => ({ ...prev, advanced: { ...prev.advanced, managerIds: [] } }));
      setManagerValidationPending(false);
    }
  }, [
    managerValidationPending,
    canPickManager,
    managerOptionsQuery.isSuccess,
    managerOptionsQuery.isError,
    managerOptionsQuery.data,
  ]);

  const persisted = useMemo(() => persistedFromUi(uiState), [uiState]);
  const canonicalQuery = useMemo(
    () => serializePersistedFiltersToCanonicalQuery(persisted),
    [persisted],
  );

  // Track the most recent canonical query we wrote to the URL so Strict
  // Mode's double-effect run does not push two identical replace navigations.
  const lastWrittenUrlRef = useRef<string | null>(null);
  // Same idea for localStorage: avoid pointless writes when nothing changed.
  const lastWrittenStorageRef = useRef<string | null>(null);

  useEffect(() => {
    if (managerValidationPending) {
      // While manager-options is still validating IDs, keep the URL/storage
      // untouched: the hydrated chips/panel are visible but the list query
      // is gated, and writing the unvalidated set would create a brief
      // window where a refresh would re-hydrate the same stale IDs.
      return;
    }
    // INVARIANT (#630 AC-2 / FR-8): the URL writer is idempotent. Build
    //   the proposed next params and compare to the current URL; skip the
    //   `replace` navigation entirely when nothing actually changes. Covers
    //   both Strict Mode's double-effect run AND the initial mount where a
    //   shared URL already arrived in canonical form. Without this guard a
    //   pointless `replace` navigation re-runs the route loader / clears
    //   scroll-restoration state on every render of the page.
    const next = applyCanonicalFilterParams(searchParams, persisted);
    preserveRequestDrawerParams(searchParams, next);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    lastWrittenUrlRef.current = canonicalQuery;
    // WHY: re-check auth at write time. A shared URL can reach this effect
    //   before refresh-cookie bootstrap completes, and a cross-tab logout
    //   may have cleared auth between render and effect. Either case must
    //   skip the storage write so an anonymous flow does not seed
    //   `magimp:filters:requests`.
    const user = useAuthStore.getState().user;
    if (user === null) return;
    if (lastWrittenStorageRef.current === canonicalQuery) return;
    safeWriteLocalStorage(canonicalQuery);
    lastWrittenStorageRef.current = canonicalQuery;
  }, [persisted, canonicalQuery, managerValidationPending, searchParams, setSearchParams]);

  const toggleStatus = useCallback((id: 'all' | RequestStatus) => {
    setUiState((prev) => {
      if (id === 'all') return { ...prev, selectedStatuses: [] };
      const next = prev.selectedStatuses.includes(id)
        ? prev.selectedStatuses.filter((x) => x !== id)
        : [...prev.selectedStatuses, id];
      return { ...prev, selectedStatuses: next };
    });
  }, []);

  const toggleSlaState = useCallback((id: 'hot' | 'overdue') => {
    setUiState((prev) => {
      const next = prev.selectedSlaStates.includes(id)
        ? prev.selectedSlaStates.filter((x) => x !== id)
        : [...prev.selectedSlaStates, id];
      return { ...prev, selectedSlaStates: next };
    });
  }, []);

  const toggleUnread = useCallback(() => {
    setUiState((prev) => ({ ...prev, unreadOnly: !prev.unreadOnly }));
  }, []);

  const toggleKind = useCallback((kind: RequestKind) => {
    setUiState((prev) => ({
      ...prev,
      selectedKind: prev.selectedKind === kind ? null : kind,
    }));
  }, []);

  const setAdvanced = useCallback((next: AdvancedFilterState) => {
    setUiState((prev) => ({ ...prev, advanced: next }));
  }, []);

  const clearAdvanced = useCallback(() => {
    // INVARIANT (#630): advanced-only reset. Status, SLA, and unread chips
    //   stay as-is — they are topbar chips, not part of the advanced panel.
    setUiState((prev) => ({ ...prev, advanced: EMPTY_ADVANCED }));
  }, []);

  const wireFilters = useMemo<Partial<RequestFilters> | null>(
    () => (managerValidationPending ? null : persistedToWireFilters(persisted, role)),
    [persisted, managerValidationPending, role],
  );

  return {
    selectedStatuses: uiState.selectedStatuses,
    toggleStatus,
    selectedSlaStates: uiState.selectedSlaStates,
    toggleSlaState,
    unreadOnly: uiState.unreadOnly,
    toggleUnread,
    selectedKind: uiState.selectedKind,
    toggleKind,
    advanced: uiState.advanced,
    setAdvanced,
    clearAdvanced,
    wireFilters,
  };
}
